"use client";

import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import { NavRail, NavItem, NavRailItem } from '@/components/navigation/nav-rail';
import { Home, Database, Menu, UserPlus, Table2, MessageSquare, ArrowLeftRight, User, History } from 'lucide-react';
import { UserDropdown } from '@/components/auth/user-dropdown';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/ui';
import { createClient } from '@/lib/supabase/client';
import { useTheme } from "next-themes";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui';
import { DatasetsSidePanel } from '@/components/pages/DatasetsSidePanel';
import { AuthButtons } from '@/components/auth/auth-buttons';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { appendRedirectParam } from '@/lib/safe-redirect';
import { UpgradeModal } from '@/components/subscription/upgrade-modal';
import { trackAction } from '@/lib/tracking';
import { useUser } from '@/hooks/use-user';
import { ChangelogModal } from '@/components/changelog/changelog-modal';

// Navigation item IDs enum
enum NavItemId {
  Home = 'home',
  Datasets = 'datasets',
  View = 'view',
  Convert = 'convert'
}

export function AppNavRail() {
  const pathname = usePathname();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { user, isLoading } = useUser();
  const supabase = createClient();
  const { theme, setTheme } = useTheme();
  
  // State for the upgrade modal
  const [upgradeModal, setUpgradeModal] = React.useState<{
    openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' | 'signin_button'
  } | null>(null);

  // State for changelog modal
  const [changelogModalOpen, setChangelogModalOpen] = React.useState(false);
  
  // Determine if we should show the datasets side panel
  const showDatasetsSidePanel = pathname.startsWith('/datasets');
  
  // Determine active item based on current path
  const getActiveId = () => {
    if (pathname.startsWith('/home')) return NavItemId.Home;
    if (pathname.startsWith('/datasets')) return NavItemId.Datasets;
    if (pathname.startsWith('/app')) return NavItemId.View;
    if (pathname.startsWith('/convert')) return NavItemId.Convert;
    return NavItemId.Home; // Default to home instead of empty string
  };
  
  // Navigation items for the rail
  const navItems: NavItem[] = [
    {
      id: NavItemId.Home,
      label: 'Home',
      icon: <Home className="h-5 w-5" />,
      href: '/home',
    },
    {
      id: NavItemId.Datasets,
      label: 'Datasets',
      icon: <Database className="h-5 w-5" />,
      href: '/datasets',
      panel: <DatasetsSidePanel />,
      panelTitle: 'Datasets'
    },
    {
      id: NavItemId.View,
      label: 'View',
      icon: <Table2 className="h-5 w-5" />,
      href: '/app',
    },
    {
      id: NavItemId.Convert,
      label: 'Convert',
      icon: <ArrowLeftRight className="h-5 w-5" />,
      href: '/convert',
    },
  ];
  
  // Additional footer items
  const footerItems: NavItem[] = [
    {
      id: 'changelog',
      label: 'What\'s New',
      icon: <History className="h-4 w-4" />,
      onClick: () => setChangelogModalOpen(true)
    },
    {
      id: 'feedback',
      label: 'Feedback',
      icon: <MessageSquare className="h-4 w-4" />,
      href: 'https://tablab.featurebase.app',
      target: '_blank',
      rel: 'noopener noreferrer'
    },
    {
      id: 'theme',
      label: 'Theme',
      icon: <ThemeIcon />,
      onClick: () => setTheme(theme === "light" ? "dark" : "light")
    }
  ];
  
  // Handle upgrade modal clicks
  const handleUpgradeClick = () => {
    setUpgradeModal({ openTrigger: 'signin_button' });
    
    // Track the event
    trackAction('upgrade_modal_triggered', {
      source: 'app_sidenav',
      trigger: 'signin_button'
    });
  };
  
  if (!isLoading) {
    const loginUrl = appendRedirectParam({
      baseUrl: '/auth/login',
      redirectUrl: pathname
    });
    
    footerItems.push({
      id: 'user',
      label: user ? 'Profile' : 'Sign In',
      icon: user ? <UserDropdown className="h-8 w-8" /> : (
        <Link href={loginUrl}>
          <Avatar className="h-8 w-8 cursor-pointer hover:ring-2 hover:ring-ring transition-all">
            <AvatarFallback className="bg-muted hover:bg-muted/80">
              <User className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
        </Link>
      ),
      href: user ? undefined : loginUrl,
      onClick: user ? undefined : handleUpgradeClick
    });
  }

  return (
    <>
      {/* Mobile header with hamburger menu */}
      <div className="h-14 border-b px-4 flex items-center justify-between md:hidden">
        <span className="font-semibold">TabLab</span>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0">
            <div className="p-4 border-b">
              <span className="font-semibold">TabLab</span>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {navItems.map((item) => (
                  <Link
                    key={item.id}
                    href={item.href || '#'}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md transition-colors",
                      pathname.startsWith(item.href || '') 
                        ? "bg-primary text-primary-foreground" 
                        : ""
                    )}
                    onClick={() => setMobileNavOpen(false)}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                ))}
                
                {/* Changelog Link for Mobile */}
                <button 
                  className="flex items-center gap-3 px-3 py-2 rounded-md transition-colors hover:bg-accent/50 w-full text-left"
                  onClick={() => {
                    setChangelogModalOpen(true);
                    setMobileNavOpen(false);
                  }}
                >
                  <History className="h-5 w-5" />
                  <span>What's New</span>
                </button>
                
                {/* Submit Feedback Link for Mobile */}
                <Link
                  href="https://tablab.featurebase.app"
                  className="flex items-center gap-3 px-3 py-2 rounded-md transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileNavOpen(false)}
                >
                  <MessageSquare className="h-5 w-5" />
                  <span>Submit Feedback</span>
                </Link>
              </div>
            </div>
            <div className="p-4 border-t mt-auto flex justify-between items-center">
              <ThemeToggle />
              <AuthButtons 
                signInVariant="ghost"
                signUpVariant="default"
                showGoogleSignIn={true}
                size="sm"
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
      
      {/* Desktop navigation rail - hidden on mobile */}
      <div className="hidden md:block h-full">
        <NavRail
          items={navItems}
          defaultActiveId={getActiveId()}
          footer={
            <div className="flex flex-col items-center space-y-1">
              {footerItems.filter(item => item.id !== 'user').map(renderNavItem)}
              {!isLoading && (
                user ? (
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center">
                    <UserDropdown className="h-8 w-8" />
                  </div>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center hover:bg-accent/50 transition-colors">
                          <button onClick={handleUpgradeClick}>
                            <Avatar className="h-8 w-8 cursor-pointer hover:ring-2 hover:ring-ring transition-all">
                              <AvatarFallback className="bg-muted hover:bg-muted/80">
                                <User className="h-4 w-4" />
                              </AvatarFallback>
                            </Avatar>
                          </button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={10}>
                        Sign In
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )
              )}
            </div>
          }
        />
      </div>
      <UpgradeModal
        open={upgradeModal !== null}
        onClose={() => setUpgradeModal(null)}
        openTrigger={upgradeModal?.openTrigger}
      />
      <ChangelogModal 
        open={changelogModalOpen} 
        onOpenChange={setChangelogModalOpen}
      />
    </>
  );
}

// Helper function to render nav items consistently
function renderNavItem(item: NavItem) {
  return (
    <div key={item.id} className="w-full">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <NavRailItem
              label={item.label}
              icon={item.icon}
              href={item.href}
              onClick={item.onClick}
              target={item.target}
              rel={item.rel}
            />
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10}>
            {item.label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

// Custom theme toggle icon component without button
function ThemeIcon({ className }: { className?: string }) {
  const { theme } = useTheme();
  
  return (
    <div className={cn("relative flex items-center justify-center", className)}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0"
      >
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </svg>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    </div>
  );
} 